import { cachedInventoryRow } from '../inventory/cached-inventory-trees.js';
import { type JanusCtx, type Row, handBack, insertRoll, int, itemField, refuse, upper } from './context.js';
import type { JanusInput } from './janus.js';
import { phpJsonInt } from '../compile/php-json-values.js';

/** A thinned inventory instance inherits the template's unit-use flag, as legacy's template merge did. */
export function consumesUnitPerUse(held: Row, read: Pick<JanusCtx['read'], 'value' | 'definition' | 'inventoryField'>, ability?: Row): boolean | null {
  const own = read.value(held, 'consumes_unit_per_use');
  if (own != null) return phpJsonInt(own) !== 0;
  const templateId = upper(read.inventoryField?.(held, 'inventory_item_id'));
  const template = templateId === upper(itemField(ability ?? null, 'item_id')) ? ability ?? null : read.definition(templateId);
  return template ? phpJsonInt(read.value(template, 'consumes_unit_per_use')) !== 0 : null;
}

/** Direct inventory provenance wins over a choice bar's source; settlement pays one unit per cast. */
export function consumeNestedUnitPerUse(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  const id = upper(input.useAbilityVariable1 === 'nested_inventory_source' ? input.useAbilityVariable2 : input.consumeInventoryId);
  if (!id || ctx.consumedUnitSources?.has(id)) return;
  // Ranged ammunition already paid/recovered this exact physical unit in the same cast.
  if (ctx.weaponInventoryChanges?.some(change => upper(change.inventory_id) === id
      && change.before.item_qty > change.after.item_qty)) return;
  const held = cachedInventoryRow(caster, id) ?? cachedInventoryRow(ctx.encounter, id) ?? ctx.read.resolveInventory?.(id);
  if (!held) handBack('consumable:source-not-held');
  const unitUse = input.consumeInventoryUnit || consumesUnitPerUse(held, ctx.read, ability);
  if (unitUse === null) handBack('consumable:template-not-held');
  if (!unitUse) return;
  const before = { possess_id: String(ctx.read.inventoryField?.(held, 'possess_id') ?? ''),
    item_qty: int(ctx.read.inventoryField?.(held, 'item_qty') ?? 1),
    inventory_deleted: int(ctx.read.inventoryField?.(held, 'inventory_deleted') ?? 0) };
  const remaining = before.item_qty - 1;
  if (before.inventory_deleted || remaining < 0) refuse('You have no units left of this item to use.');
  (ctx.weaponInventoryChanges ??= []).push({ inventory_id: id, before,
    after: { item_qty: remaining, inventory_deleted: remaining === 0 ? 1 : 0 }, log_type: 'inventory_deleted' });
  (ctx.consumedUnitSources ??= new Set()).add(id);
  insertRoll(ctx, { roll_type_1: 'inventory_deleted', ...(input.consumeInventoryUnit ? { roll_type_2: 'consumed' } : {}),
    roll_item_id: id, by: caster, roll_result: 1, logMessage: false });
}
